import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserCog, ShieldAlert, LogOut, RotateCcw, Trash2, Gift, KeyRound, MailCheck, Ban, CheckCircle2 } from "lucide-react";

const SOURCE_LABEL = {
  paid: ["Paid", "text-donkey-green"], complimentary: ["Complimentary", "text-donkey-orange"],
  both: ["Paid + Comp", "text-donkey-green"], legacy: ["Legacy", "text-donkey-amber"],
  none: ["Free", "text-donkey-grey"],
};
const STATUS_LABEL = {
  active: "text-donkey-green", restricted: "text-donkey-amber", suspended: "text-donkey-amber",
  blocked: "text-donkey-red", anonymised: "text-donkey-grey", deleted: "text-donkey-grey",
};
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtDate = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return iso; } };
const DURATIONS = [7, 14, 30];

export default function AdminAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [restrictable, setRestrictable] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [reason, setReason] = useState("");
  const [caps, setCaps] = useState([]);
  const [newStatus, setNewStatus] = useState("active");
  const [removeBiz, setRemoveBiz] = useState("");

  // grant complimentary form
  const [gTier, setGTier] = useState("prove");
  const [gDuration, setGDuration] = useState(14);
  const [gCustomDate, setGCustomDate] = useState("");
  const [gReason, setGReason] = useState("");
  const [gNote, setGNote] = useState("");
  const [gConfirm, setGConfirm] = useState("");

  // active grant management
  const [egTier, setEgTier] = useState("");
  const [egDate, setEgDate] = useState("");
  const [egNote, setEgNote] = useState("");

  // generic confirm dialog {title, body, warn, onConfirm}
  const [confirm, setConfirm] = useState(null);

  // high-risk dialog
  const [hr, setHr] = useState(null);
  const [hrConfirm, setHrConfirm] = useState("");
  const [hrReason, setHrReason] = useState("");
  const [hrPassword, setHrPassword] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/accounts", { params: { q, status: statusFilter } });
    setAccounts(data.accounts); setStatuses(data.statuses); setRestrictable(data.restrictable);
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (uid) => {
    const { data } = await api.get(`/admin/accounts/${uid}`);
    setDetail(data); setReason("");
    setCaps(data.user.restricted_capabilities || []);
    setNewStatus(data.user.account_status === "restricted" ? "restricted" : "active");
    setRemoveBiz("");
    setGTier("prove"); setGDuration(14); setGCustomDate(""); setGReason(""); setGNote(""); setGConfirm("");
    const ag = (data.comp_history || []).find((g) => g.id === data.active_grant_id);
    setEgTier(ag?.tier || ""); setEgDate(""); setEgNote(ag?.notes || "");
  };

  const refreshDetail = () => detail && openDetail(detail.user.id);

  const apply = async (fn, msg = "Done.") => {
    try { await fn(); toast.success(msg); await refreshDetail(); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed."); }
  };

  const activeGrant = detail ? (detail.comp_history || []).find((g) => g.id === detail.active_grant_id) : null;

  // ---- panel actions ----
  const setStatus = () => apply(() => api.post(`/admin/accounts/${detail.user.id}/status`, { status: newStatus, reason }));
  const restrict = () => apply(() => api.post(`/admin/accounts/${detail.user.id}/restrict`, { capabilities: caps, reason }));
  const revokeSessions = () => apply(() => api.post(`/admin/accounts/${detail.user.id}/revoke-sessions`, { reason }));
  const resetUsage = () => { if (!reason) return toast.error("A reason is required."); apply(() => api.post(`/admin/accounts/${detail.user.id}/reset-usage`, { reason })); };
  const removeFromBiz = () => { if (!removeBiz) return toast.error("Pick a business."); apply(() => api.post(`/admin/accounts/${detail.user.id}/remove-from-business`, { business_id: removeBiz, reason })); };

  const expiryFromForm = () => {
    if (gCustomDate) return new Date(`${gCustomDate}T23:59:59`).toISOString();
    return new Date(Date.now() + gDuration * 86400000).toISOString();
  };

  const submitGrant = () => {
    if (!gReason.trim()) return toast.error("A reason is required to grant complimentary access.");
    if (gConfirm.trim() !== "GRANT") return toast.error('Type GRANT (in capitals) to confirm.');
    apply(() => api.post(`/admin/comp/grants`, {
      user_id: detail.user.id, tier: gTier, expiry_date: expiryFromForm(),
      reason: gReason.trim(), notes: gNote.trim(),
    }), "Complimentary access granted.");
  };

  const editGrant = (patch, msg) => {
    if (!reason.trim()) return toast.error("A reason is required for grant changes (use the reason field).");
    apply(() => api.patch(`/admin/comp/grants/${activeGrant.id}`, { ...patch, reason: reason.trim() }), msg);
  };
  const changeGrantPlan = () => { if (egTier === activeGrant.tier) return toast.error("Pick a different plan."); editGrant({ tier: egTier }, "Complimentary plan changed."); };
  const changeGrantExpiry = () => { if (!egDate) return toast.error("Pick a new expiry date."); editGrant({ expiry_date: new Date(`${egDate}T23:59:59`).toISOString() }, "Expiry updated."); };
  const saveGrantNote = () => editGrant({ notes: egNote.trim() }, "Note saved.");
  const revokeGrant = () => {
    setConfirm({
      title: "Revoke complimentary access", warn: true,
      body: "The user returns to their genuine paid plan if any, otherwise Free. Their interviews, reports and businesses are preserved. Paid billing is untouched.",
      onConfirm: () => {
        if (!reason.trim()) { toast.error("A reason is required to revoke."); return; }
        apply(() => api.post(`/admin/comp/grants/${activeGrant.id}/revoke`, { reason: reason.trim() }), "Complimentary access revoked.");
        setConfirm(null);
      },
    });
  };

  const verifyEmail = () => {
    setConfirm({
      title: "Mark email verified", warn: true,
      body: "Only do this if you have independently confirmed the user controls this email address. This bypasses normal verification and is recorded in the audit log.",
      onConfirm: () => {
        if (!reason.trim()) { toast.error("A reason is required."); return; }
        apply(() => api.post(`/admin/accounts/${detail.user.id}/verify-email`, { reason: reason.trim() }), "Email marked verified.");
        setConfirm(null);
      },
    });
  };
  const sendPasswordReset = () => {
    setConfirm({
      title: "Send password reset", warn: false,
      body: "Generates a single-use, 1-hour reset link delivered through the normal reset flow. You will not see or set the password.",
      onConfirm: () => {
        apply(() => api.post(`/admin/accounts/${detail.user.id}/send-password-reset`, { reason: reason.trim() }), "Password reset link generated.");
        setConfirm(null);
      },
    });
  };

  // quick suspend/restore from list or panel
  const quickStatus = (uid, toStatus) => {
    const label = toStatus === "suspended" ? "Suspend" : "Restore";
    setConfirm({
      title: `${label} account`, warn: toStatus === "suspended",
      needReason: toStatus === "suspended",
      body: toStatus === "suspended"
        ? "The user is signed out immediately and blocked from signing in until restored. Their data is preserved."
        : "The user regains normal access.",
      onConfirm: (r) => {
        if (toStatus === "suspended" && !(r || "").trim()) { toast.error("A reason is required to suspend."); return; }
        apply(() => api.post(`/admin/accounts/${uid}/status`, { status: toStatus, reason: r || "" }), `${label}d.`);
        setConfirm(null);
      },
    });
  };

  const openHighRisk = async (action) => {
    try {
      const { data } = await api.get(`/admin/accounts/${detail.user.id}/impact`);
      setHr({ action, impact: data }); setHrConfirm(""); setHrReason(""); setHrPassword("");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load impact."); }
  };
  const submitHighRisk = async () => {
    try {
      await api.post(`/admin/accounts/${detail.user.id}/high-risk`, {
        action: hr.action, confirm: hrConfirm, reason: hrReason, admin_password: hrPassword,
      });
      toast.success(`Account ${hr.action}d.`);
      setHr(null); await refreshDetail(); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed."); }
  };

  return (
    <div className="space-y-5" data-testid="admin-accounts">
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" className="bg-donkey-deep border-white/15 text-donkey-warm max-w-[240px]" data-testid="acct-search" />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="acct-status-filter">
          <option value="">all statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="card-donkey rounded-2xl overflow-hidden">
        {accounts.map((a) => {
          const [sl, sc] = SOURCE_LABEL[a.entitlement_source] || SOURCE_LABEL.none;
          return (
            <div key={a.id} className="flex flex-wrap items-center gap-3 px-4 sm:px-5 py-3 border-b border-white/5 last:border-0 text-sm" data-testid={`acct-row-${a.id}`}>
              <span className="text-donkey-warm truncate flex-1 min-w-[150px]">{a.email}{a.role === "admin" && <span className="ml-1 text-xs text-donkey-orange">· admin</span>}</span>
              <span className={`text-xs capitalize ${STATUS_LABEL[a.account_status]}`}>{a.account_status}</span>
              <span className={`text-xs ${sc}`} data-testid={`acct-source-${a.id}`}>{sl}{a.effective_plan && a.effective_plan !== "free" ? ` · ${cap(a.effective_plan)}` : ""}</span>
              {a.comp_expiry && <span className="text-xs text-donkey-grey">exp {fmtDate(a.comp_expiry)}</span>}
              {a.restricted && <span className="text-xs text-donkey-amber">restricted</span>}
              <div className="flex items-center gap-2 ml-auto">
                {a.role !== "admin" && (a.account_status === "suspended"
                  ? <Button size="sm" variant="outline" onClick={() => quickStatus(a.id, "active")} className="h-8 border-white/15 bg-transparent text-donkey-green" data-testid={`acct-restore-${a.id}`}>Restore</Button>
                  : <Button size="sm" variant="outline" onClick={() => quickStatus(a.id, "suspended")} className="h-8 border-white/15 bg-transparent text-donkey-red" data-testid={`acct-suspend-${a.id}`}>Suspend</Button>)}
                <Button size="sm" onClick={() => openDetail(a.id)} className="h-8 bg-donkey-orange text-donkey-black font-semibold" data-testid={`acct-manage-${a.id}`}>Manage</Button>
              </div>
            </div>
          );
        })}
        {accounts.length === 0 && <p className="p-5 text-sm text-donkey-grey">No accounts match.</p>}
      </div>

      {/* Detail / manage panel */}
      {detail && (
        <div className="card-donkey rounded-2xl p-5 sm:p-6 space-y-5" data-testid="acct-detail">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0"><UserCog className="w-5 h-5 text-donkey-orange shrink-0" /><h3 className="font-heading font-semibold text-donkey-warm truncate">{detail.user.email}</h3></div>
            <Button size="sm" variant="ghost" onClick={() => setDetail(null)} className="text-donkey-grey">Close</Button>
          </div>

          {/* Safe account info */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
            <Info label="Name" value={detail.user.name || "—"} />
            <Info label="User ID" value={<span className="font-mono text-xs break-all">{detail.user.id}</span>} />
            <Info label="Role" value={cap(detail.user.role)} />
            <Info label="Joined" value={fmtDate(detail.user.created_at)} />
            <Info label="Email verified" value={detail.user.email_verified ? <span className="text-donkey-green">Yes</span> : <span className="text-donkey-amber">No</span>} testid="acct-email-verified" />
            <Info label="Account status" value={<span className={`capitalize ${STATUS_LABEL[detail.user.account_status]}`}>{detail.user.account_status}</span>} />
            <Info label="Entitlement source" value={cap(detail.entitlement.source)} testid="acct-source" />
            <Info label="Effective plan" value={detail.entitlement.effective_plan_name} testid="acct-effective-plan" />
            <Info label="Paid plan" value={`${cap(detail.entitlement.paid_plan)} · ${detail.entitlement.status}`} />
            <Info label="Interviews" value={detail.counts?.interviews ?? 0} />
            <Info label="Reports" value={detail.counts?.reports ?? 0} />
            <Info label="Businesses" value={detail.counts?.businesses ?? 0} />
          </div>

          {detail.entitlement.complimentary && (
            <div className="p-3 rounded-lg bg-donkey-orange/10 border border-donkey-orange/30 text-sm text-donkey-warm" data-testid="acct-comp-info">
              Complimentary <b>{detail.entitlement.complimentary.tier_name}</b> · {detail.entitlement.complimentary.status}
              {detail.entitlement.complimentary.no_expiry ? " · no expiry" : ` · expires ${fmtDate(detail.entitlement.complimentary.expiry_date)}`}
              {" "}<span className="text-donkey-grey">— no card required, no automatic charge.</span>
            </div>
          )}

          {/* Businesses */}
          <div>
            <p className="text-[10px] uppercase text-donkey-grey mb-1">Businesses & roles</p>
            {detail.businesses.length === 0 ? <p className="text-sm text-donkey-grey">None.</p> : (
              <div className="flex flex-wrap gap-2">
                {detail.businesses.map((b) => <span key={b.id} className="text-xs border border-white/10 rounded px-2 py-1 text-donkey-warm">{b.name} · {b.role}{b.archived && " · archived"}</span>)}
              </div>
            )}
          </div>

          {/* Shared reason */}
          <div>
            <label className="text-[10px] uppercase text-donkey-grey">Reason (recorded in audit — required for sensitive actions)</label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason" className="bg-donkey-deep border-white/15 text-donkey-warm mt-1" data-testid="acct-reason" />
          </div>

          {/* Complimentary trial */}
          <div className="rounded-xl border border-donkey-orange/25 p-4" data-testid="acct-comp-section">
            <p className="text-sm font-semibold text-donkey-warm flex items-center gap-2 mb-3"><Gift className="w-4 h-4 text-donkey-orange" />Complimentary trial</p>
            {activeGrant ? (
              <div className="space-y-3 text-sm">
                <p className="text-donkey-warm">Active grant: <b>{activeGrant.tier_name}</b> · {activeGrant.no_expiry ? "no expiry" : `expires ${fmtDate(activeGrant.expiry_date)}`}</p>
                <div className="flex flex-wrap items-end gap-2">
                  <div><label className="text-[10px] uppercase text-donkey-grey">Change plan</label>
                    <select value={egTier} onChange={(e) => setEgTier(e.target.value)} className="block bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-9 mt-1" data-testid="acct-eg-tier">
                      {(detail.tiers || []).map((t) => <option key={t} value={t}>{cap(t)}</option>)}
                    </select></div>
                  <Button size="sm" variant="outline" onClick={changeGrantPlan} className="h-9 border-white/15 bg-transparent text-donkey-warm" data-testid="acct-eg-change-plan">Apply plan</Button>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div><label className="text-[10px] uppercase text-donkey-grey">Extend / shorten expiry</label>
                    <Input type="date" value={egDate} onChange={(e) => setEgDate(e.target.value)} className="block bg-donkey-deep border-white/15 text-donkey-warm mt-1 h-9 w-[170px]" data-testid="acct-eg-date" /></div>
                  <Button size="sm" variant="outline" onClick={changeGrantExpiry} className="h-9 border-white/15 bg-transparent text-donkey-warm" data-testid="acct-eg-apply-expiry">Apply expiry</Button>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[180px]"><label className="text-[10px] uppercase text-donkey-grey">Internal note</label>
                    <Input value={egNote} onChange={(e) => setEgNote(e.target.value)} className="block bg-donkey-deep border-white/15 text-donkey-warm mt-1 h-9" data-testid="acct-eg-note" /></div>
                  <Button size="sm" variant="outline" onClick={saveGrantNote} className="h-9 border-white/15 bg-transparent text-donkey-warm" data-testid="acct-eg-save-note">Save note</Button>
                </div>
                <Button size="sm" variant="outline" onClick={revokeGrant} className="h-9 border-donkey-red/40 bg-transparent text-donkey-red" data-testid="acct-eg-revoke"><Ban className="w-3 h-3 mr-1" />Revoke complimentary access</Button>
              </div>
            ) : (
              <div className="space-y-3 text-sm" data-testid="acct-grant-form">
                <div className="flex flex-wrap items-end gap-3">
                  <div><label className="text-[10px] uppercase text-donkey-grey">Plan</label>
                    <select value={gTier} onChange={(e) => setGTier(e.target.value)} className="block bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-9 mt-1" data-testid="grant-tier">
                      {(detail.tiers || []).map((t) => <option key={t} value={t}>{cap(t)}</option>)}
                    </select></div>
                  <div><label className="text-[10px] uppercase text-donkey-grey">Duration</label>
                    <div className="flex gap-1 mt-1">
                      {DURATIONS.map((d) => (
                        <button key={d} onClick={() => { setGDuration(d); setGCustomDate(""); }} className={`h-9 px-3 rounded-md text-xs border ${!gCustomDate && gDuration === d ? "bg-donkey-orange text-donkey-black border-donkey-orange" : "border-white/15 text-donkey-warm"}`} data-testid={`grant-dur-${d}`}>{d}d</button>
                      ))}
                    </div></div>
                  <div><label className="text-[10px] uppercase text-donkey-grey">Or custom expiry</label>
                    <Input type="date" value={gCustomDate} onChange={(e) => setGCustomDate(e.target.value)} className="block bg-donkey-deep border-white/15 text-donkey-warm mt-1 h-9 w-[170px]" data-testid="grant-custom-date" /></div>
                </div>
                <Input value={gReason} onChange={(e) => setGReason(e.target.value)} placeholder="reason (required, recorded in audit)" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="grant-reason" />
                <Input value={gNote} onChange={(e) => setGNote(e.target.value)} placeholder="internal note (optional)" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="grant-note" />
                <div className="p-3 rounded-lg bg-donkey-deep border border-white/10 text-donkey-grey text-xs" data-testid="grant-preview">
                  Preview: <span className="text-donkey-warm">{cap(gTier)}</span> complimentary access, no card required, expiring <span className="text-donkey-warm">{fmtDate(expiryFromForm())}</span>. No Stripe object or charge is created.
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Input value={gConfirm} onChange={(e) => setGConfirm(e.target.value)} placeholder="type GRANT to confirm" className="bg-donkey-deep border-white/15 text-donkey-warm max-w-[200px]" data-testid="grant-confirm" />
                  <Button size="sm" onClick={submitGrant} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="grant-submit"><CheckCircle2 className="w-3 h-3 mr-1" />Grant access</Button>
                </div>
              </div>
            )}
          </div>

          {/* Account safe controls */}
          <div className="flex flex-wrap items-center gap-2">
            <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="acct-status-select">
              {["active", "restricted", "suspended", "blocked"].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <Button size="sm" onClick={setStatus} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="acct-set-status">Apply status</Button>
            <Button size="sm" variant="outline" onClick={verifyEmail} className="border-white/15 bg-transparent text-donkey-warm" data-testid="acct-verify-email"><MailCheck className="w-3 h-3 mr-1" />Mark verified</Button>
            <Button size="sm" variant="outline" onClick={sendPasswordReset} className="border-white/15 bg-transparent text-donkey-warm" data-testid="acct-password-reset"><KeyRound className="w-3 h-3 mr-1" />Send password reset</Button>
            <Button size="sm" variant="outline" onClick={revokeSessions} className="border-white/15 bg-transparent text-donkey-warm" data-testid="acct-revoke-sessions"><LogOut className="w-3 h-3 mr-1" />Revoke sessions</Button>
            <Button size="sm" variant="outline" onClick={resetUsage} className="border-white/15 bg-transparent text-donkey-warm" data-testid="acct-reset-usage"><RotateCcw className="w-3 h-3 mr-1" />Reset usage</Button>
          </div>

          {/* Capability restrictions */}
          <div>
            <p className="text-[10px] uppercase text-donkey-grey mb-2 flex items-center gap-1"><ShieldAlert className="w-3 h-3" />Restrict expensive capabilities</p>
            <div className="flex flex-wrap gap-2 mb-2">
              {restrictable.map((c) => (
                <label key={c} className="text-xs flex items-center gap-1 text-donkey-warm/90 border border-white/10 rounded px-2 py-1">
                  <input type="checkbox" checked={caps.includes(c)} onChange={(e) => setCaps(e.target.checked ? [...caps, c] : caps.filter((x) => x !== c))} data-testid={`acct-cap-${c}`} />
                  {c}
                </label>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={restrict} className="border-white/15 bg-transparent text-donkey-warm" data-testid="acct-apply-restrict">Apply restrictions</Button>
          </div>

          {/* Remove from business */}
          {detail.businesses.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <select value={removeBiz} onChange={(e) => setRemoveBiz(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="acct-remove-biz-select">
                <option value="">remove from business…</option>
                {detail.businesses.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.role})</option>)}
              </select>
              <Button size="sm" variant="outline" onClick={removeFromBiz} className="border-white/15 bg-transparent text-donkey-warm" data-testid="acct-remove-biz">Remove</Button>
            </div>
          )}

          {/* Comp history */}
          <div>
            <p className="text-[10px] uppercase text-donkey-grey mb-1">Complimentary history</p>
            {detail.comp_history.length === 0 ? <p className="text-sm text-donkey-grey">None.</p> : (
              <div className="space-y-1">
                {detail.comp_history.map((g) => (
                  <div key={g.id} className="text-xs text-donkey-grey">{g.tier_name} · {g.status} · {g.no_expiry ? "no expiry" : fmtDate(g.expiry_date)} · {g.campaign || "no campaign"} · {g.reason}</div>
                ))}
              </div>
            )}
          </div>

          {/* High-risk */}
          <div className="pt-3 border-t border-donkey-red/20 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openHighRisk("anonymise")} className="border-donkey-red/40 bg-transparent text-donkey-red" data-testid="acct-anonymise"><Trash2 className="w-3 h-3 mr-1" />Anonymise…</Button>
            <Button size="sm" variant="outline" onClick={() => openHighRisk("delete")} className="border-donkey-red/40 bg-transparent text-donkey-red" data-testid="acct-delete"><Trash2 className="w-3 h-3 mr-1" />Delete…</Button>
          </div>
        </div>
      )}

      {/* Generic confirmation dialog */}
      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="bg-donkey-deep border-white/10 text-donkey-warm" data-testid="acct-confirm-dialog">
          <DialogHeader><DialogTitle className={confirm?.warn ? "text-donkey-amber flex items-center gap-2" : "text-donkey-warm"}>{confirm?.warn && <ShieldAlert className="w-5 h-5" />}{confirm?.title}</DialogTitle></DialogHeader>
          {confirm && (
            <div className="space-y-3 text-sm">
              <p className="text-donkey-grey">{confirm.body}</p>
              {confirm.needReason && (
                <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason (required)" className="bg-donkey-black border-white/15 text-donkey-warm" data-testid="confirm-reason" />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirm(null)} className="text-donkey-grey">Cancel</Button>
            <Button onClick={() => confirm.onConfirm(reason)} className={`${confirm?.warn ? "bg-donkey-red text-white" : "bg-donkey-orange text-donkey-black"} font-semibold`} data-testid="confirm-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* High-risk confirmation dialog */}
      <Dialog open={!!hr} onOpenChange={(o) => !o && setHr(null)}>
        <DialogContent className="bg-donkey-deep border-white/10 text-donkey-warm" data-testid="acct-highrisk-dialog">
          <DialogHeader><DialogTitle className="text-donkey-red flex items-center gap-2"><ShieldAlert className="w-5 h-5" />Confirm {hr?.action}</DialogTitle></DialogHeader>
          {hr && (
            <div className="space-y-3 text-sm">
              <p className="text-donkey-grey">{hr.impact.effect[hr.action]}</p>
              {!hr.impact.can_proceed && (
                <p className="text-donkey-red text-xs">Blocked: this user solely owns a business with other members. Transfer ownership first.</p>
              )}
              <p className="text-xs text-donkey-grey">Preserved: {hr.impact.preserved.join(", ")}.</p>
              <div>
                <label className="text-xs text-donkey-grey">Type <span className="font-mono text-donkey-warm">{hr.action.toUpperCase()}</span> to confirm</label>
                <Input value={hrConfirm} onChange={(e) => setHrConfirm(e.target.value)} className="bg-donkey-black border-white/15 text-donkey-warm mt-1" data-testid="hr-confirm" />
              </div>
              <Input value={hrReason} onChange={(e) => setHrReason(e.target.value)} placeholder="reason" className="bg-donkey-black border-white/15 text-donkey-warm" data-testid="hr-reason" />
              <Input type="password" value={hrPassword} onChange={(e) => setHrPassword(e.target.value)} placeholder="re-enter your admin password" className="bg-donkey-black border-white/15 text-donkey-warm" data-testid="hr-password" />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHr(null)} className="text-donkey-grey">Cancel</Button>
            <Button onClick={submitHighRisk} disabled={!hr?.impact.can_proceed} className="bg-donkey-red text-white font-semibold disabled:opacity-40" data-testid="hr-submit">Confirm {hr?.action}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, value, testid }) {
  return (
    <div>
      <p className="text-[10px] uppercase text-donkey-grey">{label}</p>
      <p className="text-donkey-warm" data-testid={testid}>{value}</p>
    </div>
  );
}
